import React, { useEffect, useState, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabaseClient';
import { enhancedRealtimeService } from '../services/EnhancedRealtimeService';
import {
  Container,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Button,
  Dialog,
  TextField,
  Box,
  LinearProgress,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Badge,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import EditIcon from '@mui/icons-material/Edit';
import BusinessIcon from '@mui/icons-material/Business';
import PersonIcon from '@mui/icons-material/Person';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import SearchIcon from '@mui/icons-material/Search';
import Slider from '@mui/material/Slider';

// Add CSS for pulse animation
const pulseAnimation = `
  @keyframes pulse {
    0% { opacity: 1; }
    50% { opacity: 0.5; }
    100% { opacity: 1; }
  }
`;

// Inject the CSS
if (typeof document !== 'undefined') {
  const style = document.createElement('style');
  style.textContent = pulseAnimation;
  document.head.appendChild(style);
}

interface AttendanceRow {
  id: number;
  organizations: string;
  main: number | string;
  reserve: number | string;
  tstrength: string;
  cmain?: string;
  creserve?: string;
}

function getStatusAndColor(percentage: number) {
  if (percentage === 100) return { status: 'Objective Go', color: '#008000' }; // Green
  if (percentage >= 80) return { status: 'Achievable Go', color: '#FFA500' }; // Orange
  if (percentage >= 50) return { status: 'Go', color: '#FFD700' }; // Yellow
  return { status: 'No Go', color: '#FF0000' }; // Red
}

const getBarColor = (percentage: number) => {
  if (percentage === 100) return '#008000';
  if (percentage >= 80) return '#1976d2';
  if (percentage >= 50) return '#FFD700';
  return '#FF0000';
};

export default function Attendance() {
  const [rows, setRows] = useState<AttendanceRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMain, setDialogMain] = useState('');
  const [dialogReserve, setDialogReserve] = useState('');
  const [dialogError, setDialogError] = useState('');
  const [selectedRow, setSelectedRow] = useState<AttendanceRow | null>(null);
  const [updatingId, setUpdatingId] = useState<number | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [organizationFilter, setOrganizationFilter] = useState('All');
  const [statusFilter, setStatusFilter] = useState('All');
  const [percentRange, setPercentRange] = useState([0, 100]);
  
  // Enhanced real-time state
  const [realtimeStatus, setRealtimeStatus] = useState<'connected' | 'connecting' | 'disconnected'>('disconnected');
  const realtimeRef = useRef<any>(null);

  const fetchAttendance = useCallback(async () => {
    setLoading(true);
    setError('');
    const { data, error } = await supabase
      .from('attendance')
      .select('*');
    if (error) {
      setError(error.message);
    } else {
      setRows(data || []);
    }
    setLoading(false);
  }, []);

  // Enhanced real-time subscription setup
  const setupRealtimeSubscription = useCallback(async () => {
    try {
      realtimeRef.current = await enhancedRealtimeService.subscribeToChannel({
        table: 'attendance',
        event: '*',
        immediateUpdate: true,
        callback: (payload) => {
          console.log('Attendance real-time update received:', payload);
          
          // Immediate optimistic update for instant UI response
          if (payload.eventType === 'INSERT' && payload.newRecord) {
            setRows(prev => [payload.newRecord, ...prev]);
          } else if (payload.eventType === 'UPDATE' && payload.newRecord) {
            setRows(prev => prev.map(row => 
              row.id === payload.newRecord.id ? payload.newRecord : row
            ));
          } else if (payload.eventType === 'DELETE' && payload.oldRecord) {
            setRows(prev => prev.filter(row => row.id !== payload.oldRecord.id));
          }
          
          // Clear optimistic update after 500ms for faster cleanup
          setTimeout(() => {
            // setOptimisticUpdates(prev => { // Removed unused variable
            //   const newSet = new Set(prev);
            //   if (payload.newRecord) newSet.delete(payload.newRecord.id);
            //   if (payload.oldRecord) newSet.delete(payload.oldRecord.id);
            //   return newSet;
            // });
          }, 500);
        }
      });
      
      setRealtimeStatus('connected');
    } catch (error) {
      console.error('Error setting up real-time subscription:', error);
      setRealtimeStatus('disconnected');
    }
  }, []);

  // Listen for real-time events
  useEffect(() => {
    const handleRealtimeEvent = (event: CustomEvent) => {
      const { table, updates } = event.detail;
      if (table === 'attendance') {
        console.log('Processing real-time updates for attendance:', updates);
        // Additional processing if needed
      }
    };

    window.addEventListener('realtime-insert', handleRealtimeEvent as EventListener);
    window.addEventListener('realtime-update', handleRealtimeEvent as EventListener);
    window.addEventListener('realtime-delete', handleRealtimeEvent as EventListener);

    return () => {
      window.removeEventListener('realtime-insert', handleRealtimeEvent as EventListener);
      window.removeEventListener('realtime-update', handleRealtimeEvent as EventListener);
      window.removeEventListener('realtime-delete', handleRealtimeEvent as EventListener);
    };
  }, []);

  useEffect(() => {
    fetchAttendance();
    setupRealtimeSubscription();
    
    // Cleanup on unmount
    return () => {
      if (realtimeRef.current) {
        enhancedRealtimeService.cleanup();
      }
    };
  }, [fetchAttendance, setupRealtimeSubscription]);

  const handleOpenDialog = (row: AttendanceRow) => {
    setSelectedRow(row);
    setDialogMain(row.cmain && row.cmain !== '' ? row.cmain : '0');
    setDialogReserve(row.creserve && row.creserve !== '' ? row.creserve : '0');
    setDialogError('');
    setDialogOpen(true);
  };

  const handleDialogChangeMain = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDialogMain(e.target.value);
    setDialogError('');
  };
  const handleDialogChangeReserve = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDialogReserve(e.target.value);
    setDialogError('');
  };

  const handleDialogSubmit = async () => {
    if (!selectedRow) return;
    const main = parseInt(selectedRow.main as string) || 0;
    const reserve = parseInt(selectedRow.reserve as string) || 0;
    
    if (dialogMain === '' || isNaN(Number(dialogMain)) || dialogReserve === '' || isNaN(Number(dialogReserve))) {
      setDialogError('Please enter both values.');
      return;
    }
    const numMain = Number(dialogMain);
    const numReserve = Number(dialogReserve);
    
    // Validate individual values don't exceed maximums
    if (numMain > main) {
      setDialogError(`Current main strength (${numMain}) cannot exceed maximum main strength (${main})`);
      return;
    }
    if (numReserve > reserve) {
      setDialogError(`Current reserve strength (${numReserve}) cannot exceed maximum reserve strength (${reserve})`);
      return;
    }
    
    // Validate total current strength doesn't exceed total allocated strength
    const totalCurrent = numMain + numReserve;
    const totalAllocated = main + reserve;
    if (totalCurrent > totalAllocated) {
      setDialogError(`Total cstrength (${totalCurrent}) cannot exceed total allocated strength (${totalAllocated})`);
      return;
    }
    
    // Optimistic update for instant UI response
    setRows(prev => prev.map(row => 
      row.id === selectedRow.id 
        ? { ...row, cmain: numMain.toString(), creserve: numReserve.toString() }
        : row
    ));
    // setOptimisticUpdates(prev => new Set([...prev, selectedRow.id])); // Removed unused variable
    
    setUpdatingId(selectedRow.id);
    try {
      const { error } = await supabase
        .from('attendance')
        .update({ 
          cmain: numMain.toString(), 
          creserve: numReserve.toString()
        })
        .eq('id', selectedRow.id);
      
      if (error) {
        setDialogError(error.message);
        // Rollback optimistic update on error
        fetchAttendance();
      } else {
        setSuccessMsg('Current strength updated!');
        setDialogOpen(false);
        // Clear success message after 3 seconds
        setTimeout(() => {
          setSuccessMsg('');
        }, 3000);
        
        // Clear optimistic update after successful update
        setTimeout(() => {
          // setOptimisticUpdates(prev => { // Removed unused variable
          //   const newSet = new Set(prev);
          //   newSet.delete(selectedRow.id);
          //   return newSet;
          // });
        }, 2000);
      }
    } catch (error) {
      console.error('Error updating attendance:', error);
      setDialogError('Failed to update attendance');
      // Rollback optimistic update on error
      fetchAttendance();
    } finally {
      setUpdatingId(null);
    }
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setDialogError('');
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    setError('');
    const { data, error } = await supabase
      .from('attendance')
      .select('*');
    if (error) {
      setError(error.message);
    } else {
      setRows(data || []);
    }
    setRefreshing(false);
  };

  // Get unique organization names
  const organizationNames = ['All', ...Array.from(new Set(rows.map(row => row.organizations)))];
  const statusOptions = ['All', 'Objective Go', 'Achievable Go', 'Go', 'No Go'];
  // Helper to get status from percentage
  function getStatus(percent: number) {
    if (percent === 100) return 'Objective Go';
    if (percent >= 80) return 'Achievable Go';
    if (percent >= 50) return 'Go';
    return 'No Go';
  }
  // Filtered rows
  const filteredRows = rows.filter(row => {
    const main = parseInt(row.main as string) || 0;
    const reserve = parseInt(row.reserve as string) || 0;
    const totalStrength = main + reserve;
    const cmain = parseInt(row.cmain || '0') || 0;
    const creserve = parseInt(row.creserve || '0') || 0;
    const cstrength = cmain + creserve;
    const percent = totalStrength > 0 ? Math.round((cstrength / totalStrength) * 100) : 0;
    const status = getStatus(percent);
    return (
      (organizationFilter === 'All' || row.organizations === organizationFilter) &&
      (statusFilter === 'All' || status === statusFilter) &&
      percent >= percentRange[0] && percent <= percentRange[1] &&
      row.organizations.toLowerCase().includes(searchQuery.toLowerCase())
    );
  });

  // Add statistics
  const totalOrgs = rows.length;
  const totalStrength = rows.reduce((acc, row) => acc + (parseInt(row.main as string) || 0) + (parseInt(row.reserve as string) || 0), 0);
  const totalCurrent = rows.reduce((acc, row) => acc + (parseInt(row.cmain || '0') || 0) + (parseInt(row.creserve || '0') || 0), 0);
  const avgPercent = totalStrength > 0 ? Math.round((totalCurrent / totalStrength) * 100) : 0;

  return (
    <Container maxWidth="lg" sx={{ py: 4, background: '#f0f2f5', minHeight: '100vh' }}>
      {/* Real-time Status Indicator */}
      <Box sx={{ position: 'relative', mb: 2 }}>
        <Badge
          badgeContent=""
          color={realtimeStatus === 'connected' ? 'success' : realtimeStatus === 'connecting' ? 'warning' : 'error'}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            zIndex: 1,
            '& .MuiBadge-badge': {
              width: 12,
              height: 12,
              borderRadius: '50%',
              animation: realtimeStatus === 'connecting' ? 'pulse 2s infinite' : 'none',
            }
          }}
        >
          <Box sx={{ width: 8, height: 8 }} />
        </Badge>
      </Box>
      
      {/* Filter & Search Row */}
      <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', justifyContent: 'flex-start' }}>
        {/* Organization Filter */}
        <FormControl size="small" sx={{ minWidth: 180, background: '#fff', borderRadius: 2 }}>
          <InputLabel>Organization</InputLabel>
          <Select
            value={organizationFilter}
            label="Organization"
            onChange={e => setOrganizationFilter(e.target.value)}
          >
            {organizationNames.map(org => (
              <MenuItem key={org} value={org}>{org}</MenuItem>
            ))}
          </Select>
        </FormControl>
        {/* Status Filter */}
        <FormControl size="small" sx={{ minWidth: 180, background: '#fff', borderRadius: 2 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={statusFilter}
            label="Status"
            onChange={e => setStatusFilter(e.target.value)}
          >
            {statusOptions.map(status => (
              <MenuItem key={status} value={status}>{status}</MenuItem>
            ))}
          </Select>
        </FormControl>
        {/* Percentage Range Filter */}
        <Box sx={{ minWidth: 220, background: '#fff', borderRadius: 2, px: 2, py: 1, display: 'flex', alignItems: 'center' }}>
          <Box sx={{ mr: 2, fontWeight: 600 }}>Attendance %</Box>
          <Slider
            value={percentRange}
            onChange={(_, newValue) => setPercentRange(newValue as number[])}
            valueLabelDisplay="auto"
            min={0}
            max={100}
            sx={{ width: 120 }}
          />
        </Box>
        {/* Search Bar */}
        <Box sx={{ flex: 1, minWidth: 220 }}>
          <TextField
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            placeholder="Search organization..."
            variant="outlined"
            size="small"
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="primary" />
                </InputAdornment>
              ),
              sx: { borderRadius: 2, background: '#fff' },
            }}
          />
        </Box>
      </Box>
      {/* Summary Card */}
      <Box sx={{ mb: 3 }}>
        <Paper elevation={3} sx={{ borderRadius: 3, p: 2.5, background: 'linear-gradient(90deg, #e3f2fd 0%, #fff 100%)', display: 'flex', alignItems: 'center', gap: 4 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#1976d2', mb: 0.5 }}>Attendance Overview</Typography>
            <Box sx={{ display: 'flex', gap: 3, alignItems: 'center' }}>
              <Box>
                <Typography variant="body2" sx={{ color: '#888', fontWeight: 600 }}>Total Orgs</Typography>
                <Typography variant="h5" sx={{ color: '#1976d2', fontWeight: 700 }}>{totalOrgs}</Typography>
              </Box>
              <Box>
                <Typography variant="body2" sx={{ color: '#888', fontWeight: 600 }}>Total Strength</Typography>
                <Typography variant="h5" sx={{ color: '#388e3c', fontWeight: 700 }}>{totalStrength}</Typography>
              </Box>
              <Box>
                <Typography variant="body2" sx={{ color: '#888', fontWeight: 600 }}>Current</Typography>
                <Typography variant="h5" sx={{ color: '#1976d2', fontWeight: 700 }}>{totalCurrent}</Typography>
              </Box>
              <Box>
                <Typography variant="body2" sx={{ color: '#888', fontWeight: 600 }}>Avg %</Typography>
                <Typography variant="h5" sx={{ color: avgPercent === 100 ? '#008000' : avgPercent >= 80 ? '#1976d2' : avgPercent >= 50 ? '#FFD700' : '#FF0000', fontWeight: 700 }}>{avgPercent}%</Typography>
              </Box>
            </Box>
          </Box>
          <Box sx={{ flex: 1, minWidth: 180 }}>
            <Typography variant="body2" sx={{ color: '#888', fontWeight: 600, mb: 0.5 }}>Overall Attendance</Typography>
            <LinearProgress variant="determinate" value={avgPercent} sx={{ height: 12, borderRadius: 6, background: '#e0e0e0', '& .MuiLinearProgress-bar': { background: getBarColor(avgPercent) } }} />
          </Box>
        </Paper>
      </Box>

      {/* Status and Refresh Section */}
      <Box sx={{ mb: 3 }}>
        <Paper
          elevation={2}
          sx={{
            p: 2,
            borderRadius: 2,
            background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                Data Status
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {refreshing ? 'Refreshing attendance data...' : 'Ready for updates'}
              </Typography>
            </Box>
            <Button
              variant="contained"
              color="primary"
              startIcon={<RefreshIcon sx={{ fontSize: 22 }} />}
              onClick={handleRefresh}
              disabled={refreshing}
              sx={{
                fontWeight: 700,
                fontSize: 16,
                px: 3,
                py: 1.2,
                borderRadius: 2,
                boxShadow: '0 2px 8px rgba(25, 118, 210, 0.08)',
                textTransform: 'none',
                letterSpacing: 0.2,
                minWidth: 160,
                '&:hover': {
                  backgroundColor: '#1565c0'
                }
              }}
            >
              Refresh Data
            </Button>
          </Box>
        </Paper>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={60} />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>
      ) : rows.length === 0 ? (
        <Alert severity="info" sx={{ borderRadius: 2 }}>No attendance data found.</Alert>
      ) : (
        <>
          {successMsg && (
            <Alert 
              severity="success" 
              sx={{ 
                mb: 3, 
                borderRadius: 2,
                background: 'linear-gradient(135deg, #e8f5e8 0%, #d4edda 100%)',
                border: '1px solid #c3e6cb',
              }}
            >
              {successMsg}
            </Alert>
          )}
          
          {/* Status Criteria Section */}
          <Paper elevation={1} sx={{ mb: 3, p: 2, borderRadius: 2, background: '#f7f7f7' }}>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1, borderRadius: 1, border: '2px solid #008000', background: '#e8f5e9', minWidth: 120 }}>
                <Box sx={{ width: 16, height: 16, background: '#008000', borderRadius: 1, mr: 1 }} />
                <Typography sx={{ fontWeight: 700, color: '#008000', fontSize: 14 }}>Objective Go</Typography>
                <Typography sx={{ fontSize: 12 }}>= 100%</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1, borderRadius: 1, border: '2px solid #FFA500', background: '#fff8e1', minWidth: 120 }}>
                <Box sx={{ width: 16, height: 16, background: '#FFA500', borderRadius: 1, mr: 1 }} />
                <Typography sx={{ fontWeight: 700, color: '#FFA500', fontSize: 14 }}>Achievable Go</Typography>
                <Typography sx={{ fontSize: 12 }}>= 80-99%</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1, borderRadius: 1, border: '2px solid #FFD700', background: '#fffde7', minWidth: 120 }}>
                <Box sx={{ width: 16, height: 16, background: '#FFD700', borderRadius: 1, mr: 1 }} />
                <Typography sx={{ fontWeight: 700, color: '#FFD700', fontSize: 14 }}>Go</Typography>
                <Typography sx={{ fontSize: 12 }}>= 50-79%</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1, borderRadius: 1, border: '2px solid #FF0000', background: '#ffebee', minWidth: 120 }}>
                <Box sx={{ width: 16, height: 16, background: '#FF0000', borderRadius: 1, mr: 1 }} />
                <Typography sx={{ fontWeight: 700, color: '#FF0000', fontSize: 14 }}>No Go</Typography>
                <Typography sx={{ fontSize: 12 }}>&lt; 50%</Typography>
              </Box>
            </Box>
          </Paper>
          
          {/* Table Section */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {filteredRows.length === 0 ? (
              <Paper elevation={1} sx={{ p: 4, borderRadius: 3, textAlign: 'center', color: '#888' }}>
                No matching organizations.
              </Paper>
            ) : (
              filteredRows.map((row) => {
                const main = parseInt(row.main as string) || 0;
                const reserve = parseInt(row.reserve as string) || 0;
                const totalStrength = main + reserve;
                const cmain = parseInt(row.cmain || '0') || 0;
                const creserve = parseInt(row.creserve || '0') || 0;
                const cstrength = cmain + creserve;
                const percentage = totalStrength > 0 ? Math.round((cstrength / totalStrength) * 100) : 0;
                const { status, color } = getStatusAndColor(percentage);
                return (
                  <Paper key={row.id} elevation={2} sx={{ borderRadius: 3, p: 2.5, background: '#fff', boxShadow: '0 2px 12px rgba(25,118,210,0.06)', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: '#222' }}>{row.organizations}</Typography>
                      <Box sx={{ px: 2, py: 0.5, borderRadius: 2, background: color, color: '#fff', fontWeight: 700, fontSize: 15, minWidth: 80, textAlign: 'center' }}>{status}</Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 1 }}>
                      <Box sx={{ flex: 1, minWidth: 120, background: '#f6fafd', borderRadius: 2, p: 1, textAlign: 'center' }}>
                        <Typography sx={{ color: '#1976d2', fontWeight: 700, fontSize: 13 }}>Total</Typography>
                        <Typography sx={{ color: '#1976d2', fontWeight: 700, fontSize: 18 }}>{totalStrength}</Typography>
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 120, background: '#f6fafd', borderRadius: 2, p: 1, textAlign: 'center' }}>
                        <Typography sx={{ color: '#388e3c', fontWeight: 700, fontSize: 13 }}>Current</Typography>
                        <Typography sx={{ color: '#388e3c', fontWeight: 700, fontSize: 18 }}>{cstrength}</Typography>
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 120, background: '#f6fafd', borderRadius: 2, p: 1, textAlign: 'center' }}>
                        <Typography sx={{ color: '#7b1fa2', fontWeight: 700, fontSize: 13 }}>Main/Reserve</Typography>
                        <Typography sx={{ color: '#7b1fa2', fontWeight: 700, fontSize: 18 }}>{cmain} / {creserve}</Typography>
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 120, background: '#f6fafd', borderRadius: 2, p: 1, textAlign: 'center' }}>
                        <Typography sx={{ color: color, fontWeight: 700, fontSize: 13 }}>Attendance %</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                          <Typography sx={{ color: color, fontWeight: 700, fontSize: 18 }}>{percentage}%</Typography>
                          <LinearProgress variant="determinate" value={percentage} sx={{ width: 60, height: 8, borderRadius: 4, background: '#e0e0e0', '& .MuiLinearProgress-bar': { background: getBarColor(percentage) } }} />
                        </Box>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                      <Box sx={{ flex: 1, background: '#f8fafd', borderRadius: 2, p: 1, textAlign: 'center', border: '1px solid #e0e0e0' }}>
                        <Typography sx={{ fontWeight: 700, color: '#1976d2', fontSize: 13 }}>Allocated</Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 0.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <PersonIcon sx={{ color: '#1976d2', fontSize: 18 }} />
                            <Typography sx={{ color: '#1976d2', fontWeight: 700 }}>{main}</Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <PersonOutlineIcon sx={{ color: '#7b1fa2', fontSize: 18 }} />
                            <Typography sx={{ color: '#7b1fa2', fontWeight: 700 }}>{reserve}</Typography>
                          </Box>
                        </Box>
                      </Box>
                      <Box sx={{ flex: 1, background: '#f8fafd', borderRadius: 2, p: 1, textAlign: 'center', border: '1px solid #e0e0e0' }}>
                        <Typography sx={{ fontWeight: 700, color: '#388e3c', fontSize: 13 }}>Current</Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 0.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <PersonIcon sx={{ color: '#388e3c', fontSize: 18 }} />
                            <Typography sx={{ color: '#388e3c', fontWeight: 700 }}>{cmain}</Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <PersonOutlineIcon sx={{ color: '#7b1fa2', fontSize: 18 }} />
                            <Typography sx={{ color: '#7b1fa2', fontWeight: 700 }}>{creserve}</Typography>
                          </Box>
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', flex: 1 }}>
                        <Button variant="contained" size="medium" onClick={() => handleOpenDialog(row)} sx={{ fontWeight: 700, background: '#1976d2', borderRadius: 2, px: 3, minWidth: 100, '&:hover': { background: '#1565c0' } }}>Update</Button>
                      </Box>
                    </Box>
                  </Paper>
                );
              })
            )}
          </Box>
        </>
      )}

      <Dialog 
        open={dialogOpen} 
        onClose={handleDialogClose}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
            boxShadow: '0 8px 32px rgba(0,0,0,0.13)',
            maxWidth: 420,
            background: '#fff',
            p: 0,
          }
        }}
      >
        {/* Blue header with edit icon and title */}
        <Box sx={{
          background: 'linear-gradient(90deg, #2196f3 0%, #1976d2 100%)',
          borderRadius: '16px 16px 0 0',
          px: 2.5, py: 2,
          display: 'flex', alignItems: 'center', gap: 1.5
        }}>
          <EditIcon sx={{ color: 'white', fontSize: 22 }} />
          <Typography variant="h6" sx={{ fontWeight: 700, color: 'white', fontSize: 18, letterSpacing: 0.2, flex: 1 }}>
            Update Current Strength
          </Typography>
        </Box>
        <Box sx={{ p: 0 }}>
          {/* Organization name row */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 3, pt: 2, pb: 1 }}>
            <BusinessIcon sx={{ color: '#1976d2', fontSize: 20 }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1976d2', fontSize: 16, letterSpacing: 0.1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {selectedRow?.organizations}
            </Typography>
          </Box>
          {/* Allocated Strength - minimalist cards */}
          <Box sx={{ display: 'flex', gap: 2, px: 3, pb: 2 }}>
            <Box
              sx={{
                flex: 1,
                p: 1.2,
                background: '#f6fafd',
                borderRadius: 2,
                border: '1px solid #e0e0e0',
                textAlign: 'center',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              <PersonIcon sx={{ color: '#1976d2', mb: 0.5, fontSize: 20 }} />
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#1976d2', fontSize: 13, letterSpacing: 0.1 }}>
                Main
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, fontSize: 18, color: '#222', letterSpacing: 0.2 }}>
                {selectedRow ? parseInt(selectedRow.main as string) || 0 : 0}
              </Typography>
            </Box>
            <Box
              sx={{
                flex: 1,
                p: 1.2,
                background: '#f6fafd',
                borderRadius: 2,
                border: '1px solid #e0e0e0',
                textAlign: 'center',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              <PersonOutlineIcon sx={{ color: '#9c27b0', mb: 0.5, fontSize: 20 }} />
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#9c27b0', fontSize: 13, letterSpacing: 0.1 }}>
                Reserve
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, fontSize: 18, color: '#222', letterSpacing: 0.2 }}>
                {selectedRow ? parseInt(selectedRow.reserve as string) || 0 : 0}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ my: 2, borderBottom: '1.5px solid #e0e0e0', mx: 3 }} />
          {/* Input fields - clean, borderless style */}
          <Box sx={{ px: 3, pb: 2 }}>
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <PersonIcon sx={{ color: '#1976d2', fontSize: 18 }} />
                <Typography sx={{ fontWeight: 700, color: '#1976d2', fontSize: 14 }}>Current Main Strength</Typography>
              </Box>
              <TextField
                type="number"
                fullWidth
                value={dialogMain}
                onChange={handleDialogChangeMain}
                inputProps={{ min: 0, max: selectedRow ? parseInt(selectedRow.main as string) || 0 : 0 }}
                helperText={`Maximum: ${selectedRow ? parseInt(selectedRow.main as string) || 0 : 0}`}
                InputProps={{
                  style: { fontWeight: 600, fontSize: 15 },
                  disableUnderline: true,
                }}
                FormHelperTextProps={{ sx: { fontSize: 12 } }}
                sx={{ background: '#f9f9f9', borderRadius: 1, mb: 0, mt: 0, '& .MuiInputBase-root': { fontSize: 15 }, border: 'none' }}
              />
            </Box>
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <PersonOutlineIcon sx={{ color: '#9c27b0', fontSize: 18 }} />
                <Typography sx={{ fontWeight: 700, color: '#9c27b0', fontSize: 14 }}>Current Reserve Strength</Typography>
              </Box>
              <TextField
                type="number"
                fullWidth
                value={dialogReserve}
                onChange={handleDialogChangeReserve}
                inputProps={{ min: 0, max: selectedRow ? parseInt(selectedRow.reserve as string) || 0 : 0 }}
                helperText={`Maximum: ${selectedRow ? parseInt(selectedRow.reserve as string) || 0 : 0}`}
                InputProps={{
                  style: { fontWeight: 600, fontSize: 15 },
                  disableUnderline: true,
                }}
                FormHelperTextProps={{ sx: { fontSize: 12 } }}
                sx={{ background: '#f9f9f9', borderRadius: 1, mb: 0, mt: 0, '& .MuiInputBase-root': { fontSize: 15 }, border: 'none' }}
              />
            </Box>
            {dialogError && (
              <Alert 
                severity="error" 
                sx={{ 
                  mt: 2, 
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%)',
                  fontSize: 13,
                }}
              >
                {dialogError}
              </Alert>
            )}
          </Box>
          {/* Actions */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, px: 3, pb: 3 }}>
            <Button 
              onClick={handleDialogClose}
              sx={{ 
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                color: '#d32f2f',
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleDialogSubmit} 
              variant="contained" 
              disabled={updatingId === (selectedRow && selectedRow.id)}
              sx={{
                background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                px: 3,
                '&:hover': {
                  background: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
                },
              }}
            >
              {updatingId === (selectedRow && selectedRow.id) ? 'Updating...' : 'Update'}
            </Button>
          </Box>
        </Box>
      </Dialog>
    </Container>
  );
}
